import { useState } from 'react'
import { TabProps } from './types'
import { useFormatters } from '../../contexts/PreferencesContext'
import { OTIFDrilldownModal, OTDDrilldownModal } from '../drilldown'
import { SectionTourTrigger } from '../tour/SectionTourTrigger'
import { deriveSiteKPIMetrics, describeSiteKPITimeframe } from './site-kpi/siteKPIMetrics'
import { useSiteKPIDrilldowns } from './site-kpi/useSiteKPIDrilldowns'
import { SiteKPIDeliveryPanel } from './site-kpi/SiteKPIDeliveryPanel'
import { SiteKPIQualityPanel } from './site-kpi/SiteKPIQualityPanel'
import { showsOtd, showsOtif } from './deliveryMetricMode'

// CR-3837: Extended props to support supplier type filter callback
// CR-5912: the previously-embedded supplier-type filter is removed in favour
// of the page-level DashboardFilters, which carries the same 20-filter set
// the main dashboard uses. Dashboard.tsx force-opens that panel when this
// tab is active so users see the full filter row without clicking Show Filters.
// CR-5868: the tab is now shown to suppliers too. `isSupplier` only changes
// wording — the "site" axis of the by-site charts is the CUSTOMER site (the
// backend groups company_kpi by po.customer), which is what a supplier needs
// to read as "which customer sites am I delivering to". Data scoping is the
// backend's (dashboard_stats supplier branch), not this component's.
type SiteKPITabProps = TabProps & {
  /** CR-5868: true on the supplier portal — relabels the by-site charts. */
  isSupplier?: boolean
}

// CR-6287: `currency` prop dropped from the destructure — the Arrears-by-value
// title no longer interpolates it (see below). It stays on TabProps for the
// other tabs that still consume it.
// CR-5868: split into site-kpi/ panels (Delivery, Quality), a derived-metrics
// module and a drill-down hook to stay under the 500-line limit.
export function SiteKPITab({ data, filters, isSupplier = false, orderType }: SiteKPITabProps) {
  const { formatDate } = useFormatters()

  // Delivery metric mode: conditionally show OTIF and/or OTD sections
  const showOTIF = showsOtif(data.delivery_metric_mode)  // CR-8697: helper knows 'none'
  const showOTD = showsOtd(data.delivery_metric_mode)

  const [activeSiteKPITab, setActiveSiteKPITab] = useState('delivery')
  // CR-3837: Use filter from props if available, with local state for UI
  // CR-5912: supplierFilter local was used by the removed in-tab dropdown.
  // The page-level DashboardFilters owns supplierType now; this tab reads
  // filtered data through `data` and doesn't need to mirror the value.

  const metrics = deriveSiteKPIMetrics(data)
  const drill = useSiteKPIDrilldowns(data, filters, isSupplier, orderType)
  const timeframe = describeSiteKPITimeframe(filters, (iso) => formatDate(iso))

  const subTabClass = (id: string) =>
    `whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
      activeSiteKPITab === id
        ? 'border-primary text-primary'
        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
    }`

  return (
    <div className="space-y-4">
      {/* CR-5868: the window the figures cover, stated on the tab itself;
          Section Tour Trigger stays on the right. */}
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground" data-testid="site-kpi-timeframe">
          Timeframe: <span className="font-medium text-foreground">{timeframe}</span>
        </p>
        <SectionTourTrigger
          pageKey="dashboard"
          sectionKey="site-kpi"
          variant="link"
          label="Tour this tab"
        />
      </div>

      {/* CR-5912: embedded Supplier-Type-only filter removed. The page-level
          DashboardFilters (above this tab) is auto-opened on Site KPI and
          carries the same 20-filter set as the main dashboard, including
          Supplier Type. */}

      {/* Sub-tabs for Delivery and Quality */}
      <div className="bg-card rounded-lg shadow-sm">
        <div className="border-b">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button onClick={() => setActiveSiteKPITab('delivery')} className={subTabClass('delivery')}>
              Delivery
            </button>
            <button onClick={() => setActiveSiteKPITab('quality')} className={subTabClass('quality')}>
              Quality
            </button>
          </nav>
        </div>
        <div className="p-6">
          {activeSiteKPITab === 'delivery' && (
            <SiteKPIDeliveryPanel
              metrics={metrics}
              drill={drill}
              showOTIF={showOTIF}
              showOTD={showOTD}
            />
          )}

          {activeSiteKPITab === 'quality' && (
            <SiteKPIQualityPanel
              data={data}
              metrics={metrics}
              drill={drill}
              showOTIF={showOTIF}
              isSupplier={isSupplier}
            />
          )}
        </div>
      </div>

      {/* OTIF Specialized Drill-Down Modal */}
      <OTIFDrilldownModal
        isOpen={drill.otifModal.isOpen}
        onClose={drill.closeOtifModal}
        filters={drill.otifModal.filters}
        contextLabel={drill.otifModal.context}
        initialTab={drill.otifModal.initialTab}
      />

      {/* OTD Specialized Drill-Down Modal */}
      <OTDDrilldownModal
        isOpen={drill.otdModal.isOpen}
        onClose={drill.closeOtdModal}
        filters={drill.otdModal.filters}
        contextLabel={drill.otdModal.context}
        initialTab={drill.otdModal.initialTab}
      />
    </div>
  )
}
